import { useXHR } from "@sembark-travel/xhr"
import { LeadChannelXHR, TLeadChannel, useLeadCategoryOptions } from "./store"
import {
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	Button,
	Col,
	Grid,
	Inline,
	Select,
	Stack,
} from "@sembark-travel/ui-base"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

export function AddNewLeadChannel({
	onSuccess,
	onCancel,
}: {
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<NewLeadChannelForm
			onSubmit={async (values) => {
				const payload = {
					name: values.name,
					lead_channel_category: values?.lead_channel_category?.id,
				}
				await LeadChannelXHR(xhr).create(payload)
				showSnackbar(`Lead Channel ${values.name} Created Successfully`)
				onSuccess()
			}}
			onCancel={onCancel}
		/>
	)
}

type TNewLeadChannelForm = {
	name: string
	lead_channel_category?: { id: string; name: string }
}

const validationSchema = Validator.object().shape({
	name: Validator.string().required("Lead Channel is Required"),
	lead_channel_category: Validator.object().required(
		"Lead Category is required"
	),
})

export function NewLeadChannelForm({
	onSubmit,
	onCancel,
	initialValues,
}: {
	onSubmit: (values: TNewLeadChannelForm) => Promise<void>
	onCancel: () => void
	initialValues?: TNewLeadChannelForm
}) {
	const { options: categoryOptions } = useLeadCategoryOptions()

	return (
		<Form<TNewLeadChannelForm>
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			initialValues={initialValues}
			validate={validateFormValues(validationSchema)}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col md={6} sm={12}>
							<TextInputField
								label="Channel Name"
								name="name"
								placeholder="Google Ads"
							/>
						</Col>

						<Col md={5} sm={12}>
							<SelectField
								name="lead_channel_category"
								select={Select}
								options={categoryOptions}
								label="Channel Category"
								fetchOnMount
							/>
						</Col>

						<Col sm={12}>
							<Stack gap="2">
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit">
										{submitting ? "Saving..." : "Save Channel"}
									</Button>
									<Button onClick={onCancel} level="tertiary">
										Cancel
									</Button>
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

export function EditLeadChannel({
	channel,
	onSuccess,
	onCancel,
}: {
	channel: TLeadChannel
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<NewLeadChannelForm
			initialValues={{
				name: channel.name,
				lead_channel_category: {
					name: channel.lead_channel_category || "",
					id: channel.lead_channel_category || "",
				},
			}}
			onSubmit={async (values) => {
				const payload = {
					name: values.name,
					lead_channel_category: values.lead_channel_category?.id,
				}
				await LeadChannelXHR(xhr).update(channel.id, payload)
				showSnackbar(`Lead Channel ${values.name} Updated Successfully`)
				onSuccess()
			}}
			onCancel={onCancel}
		/>
	)
}
